import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import { FiChevronDown, FiChevronRight, FiEdit3, FiSearch, FiUpload } from 'react-icons/fi';
import { GiSpellBook } from 'react-icons/gi';
import { DEFAULT_MAP_TITLE, getMapDisplayTitle } from '../../../utils/mapTitle';
import { UNGROUPED_FOLDER_KEY } from '../utils/mapGrouping';
import { NEW_FOLDER_OPTION_VALUE } from '../utils/mapFolders';

const testIdValue = (value, fallback = 'item') => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed ? trimmed.replace(/[^0-9A-Za-z_-]/g, '-').toLowerCase() : fallback;
};
const mapIdOf = (map) => (typeof map?.mapId === 'string' && map.mapId.trim() ? map.mapId.trim() : null);

const SOURCES = [
  { id: 'draw', label: 'Draw it', detail: 'Map Maker', icon: FiEdit3 },
  { id: 'describe', label: 'Describe it', detail: 'AI art', icon: GiSpellBook },
  { id: 'upload', label: 'Upload', detail: 'An image you have', icon: FiUpload },
];

/** Folder picker: an existing folder, none, or a new one typed in. */
function FolderField({ id, folders, selection, value, onSelect, onType, disabled, testIdPrefix }) {
  return (
    <Form.Group>
      <Form.Label htmlFor={`${id}-select`}>Folder</Form.Label>
      <Form.Select id={`${id}-select`} size="sm" value={selection} onChange={onSelect} disabled={disabled}
        data-testid={testIdPrefix ? `${testIdPrefix}-folder-select` : undefined}>
        <option value="">No folder</option>
        {folders.map((folder) => <option key={folder} value={folder}>{folder}</option>)}
        <option value={NEW_FOLDER_OPTION_VALUE}>New folder…</option>
      </Form.Select>
      {selection === NEW_FOLDER_OPTION_VALUE && (
        <Form.Control className="mt-1" size="sm" aria-label="New folder name" placeholder="Folder name" value={value}
          onChange={onType} disabled={disabled} data-testid={testIdPrefix ? `${testIdPrefix}-folder-input` : undefined} />
      )}
    </Form.Group>
  );
}

/**
 * The DM's map window, in two modes. Library: every saved map by folder, the selected map's board, and its details
 * (name, folder, grid, image) edited in place. Create: draw a layout (Map Maker), describe a map (AI) or upload an
 * image; the result is a draft until it is saved to the library with one Save.
 */
export default function MapLibraryModal({
  show, onHide, loading, status, onDismissStatus,
  groupedMaps, folderExpansion, onToggleFolder, activeMapId, selectedMap, onSelectMap, onActivateMap, onDeleteMap,
  actionLoadingId, board, editor, draft, onDiscardDraft,
  prompt, onPromptChange, lighting, onLightingChange, generating, onGenerate, onOpenMapMaker,
  onSaveDraft, saving, saveMode, campaignReady,
}) {
  const [mode, setMode] = useState('library');
  const [source, setSource] = useState('describe');
  const [search, setSearch] = useState('');
  const [draftName, setDraftName] = useState('');
  const [draftFolderSelection, setDraftFolderSelection] = useState('');
  const [draftFolder, setDraftFolder] = useState('');
  const [draftActivate, setDraftActivate] = useState(true);
  const lastDraft = useRef(null);
  const selectedId = mapIdOf(selectedMap);
  const { state: editorState, errors: editorErrors, folders } = editor;

  // A new draft (generated here or by Map Maker) opens in Create, named after its title.
  useEffect(() => {
    if (draft && draft !== lastDraft.current) {
      setMode('create');
      setSource(draft.layout ? 'draw' : 'describe');
      setDraftName(getMapDisplayTitle(draft, ''));
      const folder = typeof draft.folder === 'string' ? draft.folder.trim() : '';
      setDraftFolderSelection(folder && folders.includes(folder) ? folder : folder ? NEW_FOLDER_OPTION_VALUE : '');
      setDraftFolder(folder);
    }
    lastDraft.current = draft;
  }, [draft, folders]);

  // The details form loads when a different map is picked (or that map's saved fields change); the upload form starts
  // empty each time Upload opens. Map list refreshes in between never reset what the DM is typing.
  const editorRef = useRef(editor);
  editorRef.current = editor;
  const selectedRef = useRef(selectedMap);
  selectedRef.current = selectedMap;
  const selectedKey = selectedMap
    ? JSON.stringify([selectedId, selectedMap.updatedAt, selectedMap.title, selectedMap.folder, selectedMap.imageUrl, selectedMap.gridColumns])
    : '';
  useEffect(() => {
    if (show && mode === 'library' && selectedRef.current) editorRef.current.beginEdit(selectedRef.current);
  }, [show, mode, selectedKey]);
  useEffect(() => {
    if (show && mode === 'create' && source === 'upload') editorRef.current.beginUpload();
  }, [show, mode, source]);

  const query = search.trim().toLowerCase();
  const visibleGroups = useMemo(() => groupedMaps
    .map((group) => ({ ...group, maps: query ? group.maps.filter((map) => getMapDisplayTitle(map, DEFAULT_MAP_TITLE).toLowerCase().includes(query)) : group.maps }))
    .filter((group) => !query || group.maps.length > 0), [groupedMaps, query]);
  const mapCount = groupedMaps.reduce((sum, group) => sum + group.maps.length, 0);

  const selectMap = (mapId) => {
    if (draft && !window.confirm('Discard the unsaved map draft?')) return;
    onSelectMap(mapId);
  };
  const submitEditor = async (event) => {
    const saved = await editor.onSubmit(event);
    if (saved && editorState.mode === 'create') setMode('library');
  };
  const saveDraft = async () => {
    const folder = draftFolderSelection === NEW_FOLDER_OPTION_VALUE ? draftFolder.trim() : draftFolderSelection;
    if (await onSaveDraft({ title: draftName.trim(), folder, activate: draftActivate })) setMode('library');
  };
  const replaceWithDraft = async () => {
    const title = getMapDisplayTitle(selectedMap, DEFAULT_MAP_TITLE);
    if (!window.confirm(`Replace the picture of “${title}” with this draft? Its fog of war starts covered again.`)) return;
    if (await onSaveDraft({ replaceMapId: selectedId })) setMode('library');
  };

  const gridOptions = useMemo(() => {
    const current = Number.parseInt(editorState.gridSelection, 10);
    return [...new Set([...editor.gridOptions, ...(Number.isInteger(current) ? [current] : [])])].sort((a, b) => a - b);
  }, [editor.gridOptions, editorState.gridSelection]);
  const gridField = (testId) => (
    <Form.Group controlId={`${testId}-grid`}>
      <Form.Label>Grid</Form.Label>
      <Form.Select size="sm" value={editorState.gridSelection} onChange={editor.onFieldChange('gridSelection')}
        disabled={editor.saving || Boolean(editorState.map?.layout)} data-testid="map-editor-grid-select">
        {gridOptions.map((size) => <option key={size} value={`${size}`}>{`${size} × ${size} squares`}</option>)}
      </Form.Select>
      {editorState.map?.layout && <Form.Text className="map-library__hint">Set by its Map Maker layout.</Form.Text>}
    </Form.Group>
  );
  const imageFields = (required) => (
    <>
      <Form.Group controlId="map-editor-image-file">
        <Form.Label>Image file</Form.Label>
        <Form.Control key={editorState.fileInputKey} type="file" accept="image/*" size="sm" onChange={editor.onFileChange}
          disabled={editor.saving} aria-describedby={editor.describedBy} isInvalid={Boolean(editorErrors.imageSource)} />
      </Form.Group>
      <Form.Group controlId="map-editor-image-url">
        <Form.Label>…or image URL</Form.Label>
        <Form.Control type="url" size="sm" placeholder="https://example.com/map.png" value={editorState.imageUrl}
          onChange={editor.onFieldChange('imageUrl')} disabled={editor.saving} aria-describedby={editor.describedBy}
          isInvalid={Boolean(editorErrors.imageSource)} />
      </Form.Group>
      <Form.Text id="map-editor-image-requirement" className="map-library__hint">
        {required ? 'Choose a file or paste a URL.' : 'Choose a new file or URL to replace the current picture.'}
      </Form.Text>
      {editorErrors.imageSource && <div id="map-editor-image-error" className="map-library__error" role="alert">{editorErrors.imageSource}</div>}
    </>
  );
  const altField = (
    <Form.Group controlId="map-editor-alt-text">
      <Form.Label>Description for screen readers</Form.Label>
      <Form.Control size="sm" placeholder="A ruined chapel in the snow" value={editorState.altText}
        onChange={editor.onFieldChange('altText')} disabled={editor.saving} />
    </Form.Group>
  );
  const nameField = (
    <Form.Group controlId="map-editor-title">
      <Form.Label>Name</Form.Label>
      <Form.Control size="sm" value={editorState.title} onChange={editor.onFieldChange('title')} disabled={editor.saving}
        isInvalid={Boolean(editorErrors.title)} placeholder="Frozen chapel" />
      {editorErrors.title && <Form.Control.Feedback type="invalid">{editorErrors.title}</Form.Control.Feedback>}
    </Form.Group>
  );
  const editorFolder = (
    <FolderField id="map-editor-folder" folders={folders} selection={editorState.folderSelection} value={editorState.folder}
      onSelect={editor.onFolderSelect} onType={editor.onFieldChange('folder')} disabled={editor.saving} testIdPrefix="map-editor" />
  );
  const submitLabel = (label) => (editor.saving ? <><Spinner as="span" animation="border" size="sm" className="me-2" aria-hidden="true" />Saving…</> : label);

  const library = (
    <div className="map-library__library">
      <aside className="map-library__sidebar" aria-label="Saved maps">
        <label className="map-library__search">
          <FiSearch aria-hidden="true" />
          <input type="search" placeholder="Search maps" aria-label="Search maps" value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        {loading ? (
          <div className="map-library__empty"><Spinner animation="border" size="sm" role="status"><span className="visually-hidden">Loading maps…</span></Spinner></div>
        ) : (
          <div data-testid="map-list" className="map-library__list">
            {mapCount === 0 && <p className="map-library__empty" data-testid="map-list-empty">No maps yet. Make one in Create.</p>}
            {mapCount > 0 && visibleGroups.length === 0 && <p className="map-library__empty">No maps match “{search.trim()}”.</p>}
            {visibleGroups.map((group) => {
              const folderTestId = testIdValue(group.key === UNGROUPED_FOLDER_KEY ? 'ungrouped' : group.key, 'folder');
              const expanded = query ? true : typeof folderExpansion[group.key] === 'boolean' ? folderExpansion[group.key] : group.key === UNGROUPED_FOLDER_KEY;
              return (
                <div key={group.key} className="map-library__folder" data-testid={`map-folder-${folderTestId}-header`}>
                  <button type="button" className="map-library__folder-toggle" aria-expanded={expanded}
                    onClick={() => onToggleFolder(group.key)} data-testid={`map-folder-toggle-${folderTestId}`}>
                    {expanded ? <FiChevronDown aria-hidden="true" /> : <FiChevronRight aria-hidden="true" />}
                    <span>{group.label}</span>
                    <span className="map-library__count" data-testid={`map-folder-count-${folderTestId}`}>
                      {group.maps.length}<span className="visually-hidden"> maps</span>
                    </span>
                  </button>
                  {expanded && group.maps.map((map, index) => {
                    const id = mapIdOf(map);
                    const key = id || `map-${group.key}-${index}`;
                    return (
                      <button key={key} type="button" className={`map-library__map${id && id === selectedId ? ' map-library__map--selected' : ''}`}
                        aria-pressed={Boolean(id && id === selectedId)} disabled={!id} onClick={() => id && selectMap(id)}
                        data-testid={`map-list-item-${key}`} data-folder-key={group.key}>
                        <span className="map-library__map-title">{getMapDisplayTitle(map, DEFAULT_MAP_TITLE)}</span>
                        {id && id === activeMapId && <span className="map-library__active" data-testid={`map-active-badge-${key}`}>Active</span>}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </aside>
      <section className="map-library__stage" aria-label="Map preview">
        {draft ? (
          <div className="map-library__draft-note">
            <p>You have an unsaved draft, <strong>{getMapDisplayTitle(draft, 'Untitled map')}</strong>.</p>
            <div><Button size="sm" variant="warning" onClick={() => setMode('create')}>Back to the draft</Button>
              <Button size="sm" variant="outline-light" onClick={onDiscardDraft}>Discard it</Button></div>
          </div>
        ) : selectedMap ? (
          <>
            <div className="map-library__stage-bar">
              <h3>{getMapDisplayTitle(selectedMap, DEFAULT_MAP_TITLE)}</h3>
              {selectedId === activeMapId ? <span className="map-library__active">Active map</span> : (
                <Button size="sm" variant="warning" disabled={!selectedId || actionLoadingId === selectedId}
                  onClick={() => onActivateMap(selectedId)} data-testid={`map-activate-button-${selectedId}`}>Set active</Button>
              )}
              <Button size="sm" variant="outline-danger" disabled={!selectedId || actionLoadingId === selectedId}
                onClick={() => onDeleteMap(selectedId)} data-testid={`map-delete-button-${selectedId}`}>Delete</Button>
            </div>
            <div className="map-library__board" data-testid="map-preview-card">{board}</div>
          </>
        ) : (
          <p className="map-library__empty">{mapCount ? 'Pick a map on the left.' : 'Your saved maps show here.'}</p>
        )}
      </section>
      {selectedMap && !draft && editorState.mode === 'rename' && mapIdOf(editorState.map) === selectedId && (
        <Form className="map-library__panel" onSubmit={submitEditor} aria-label="Map details" data-testid="map-details-form">
          <h3>Details</h3>
          {nameField}
          {editorFolder}
          {gridField('map-details')}
          <details className="map-library__replace">
            <summary>Replace the picture</summary>
            {imageFields(false)}
            {altField}
          </details>
          <Button type="submit" size="sm" variant="light" disabled={editor.saving} data-testid="map-editor-submit-button">{submitLabel('Save changes')}</Button>
        </Form>
      )}
    </div>
  );

  const replaceable = Boolean(selectedId && draft);
  const create = (
    <div className="map-library__create">
      <div className="map-library__sources">
        <div role="radiogroup" aria-label="How to make the map" className="map-library__source-list">
          {SOURCES.map(({ id, label, detail, icon: Icon }) => (
            <button key={id} type="button" role="radio" aria-checked={source === id}
              className={`map-library__source${source === id ? ' map-library__source--active' : ''}`} onClick={() => setSource(id)}>
              <Icon aria-hidden="true" /><span>{label}</span><small>{detail}</small>
            </button>
          ))}
        </div>
        {source === 'draw' && (
          <div className="map-library__panel">
            <p className="map-library__hint">Draw walls, cover and terrain on the grid and pin notes, then generate art that follows the layout. The finished map comes back here.</p>
            <Button variant="warning" size="sm" onClick={onOpenMapMaker} disabled={!campaignReady}>Open Map Maker</Button>
          </div>
        )}
        {source === 'describe' && (
          <div className="map-library__panel">
            <Form.Group controlId="map-generation-prompt">
              <Form.Label>Describe the map</Form.Label>
              <Form.Control as="textarea" rows={5} placeholder="Describe the map you want to generate" value={prompt}
                onChange={(event) => onPromptChange(event.target.value)} disabled={generating} />
            </Form.Group>
            <div className="map-library__row" role="group" aria-label="Time of day">
              <span className="map-library__hint">Time of day</span>
              {[['day', 'Day'], ['night', 'Night']].map(([id, label]) => (
                <Button key={id} size="sm" variant={lighting === id ? 'light' : 'outline-light'} aria-pressed={lighting === id}
                  onClick={() => onLightingChange(id)} disabled={generating}>{label}</Button>
              ))}
            </div>
            <Button variant="warning" size="sm" onClick={onGenerate} disabled={generating || loading || !prompt.trim()}>
              {generating ? <><Spinner as="span" animation="border" size="sm" className="me-2" aria-hidden="true" />Generating…</> : 'Generate map'}
            </Button>
          </div>
        )}
        {source === 'upload' && editorState.mode === 'create' && (
          <Form className="map-library__panel" onSubmit={submitEditor} aria-label="Upload a map" data-testid="map-upload-form">
            {nameField}
            {imageFields(true)}
            {editorFolder}
            {gridField('map-upload')}
            {altField}
            <Form.Check type="switch" id="map-editor-activate" label="Make it the active map" checked={Boolean(editorState.activateOnSave)}
              onChange={editor.onActivateChange} disabled={editor.saving} />
            <Button type="submit" size="sm" variant="warning" disabled={editor.saving} data-testid="map-editor-submit-button">{submitLabel('Save to library')}</Button>
          </Form>
        )}
      </div>
      <section className="map-library__stage" aria-label="Result">
        {source === 'upload' ? (
          editorState.imageBase64 || editorState.imageUrl ? (
            <img className="map-library__upload-preview" alt=""
              src={editorState.imageBase64 ? `data:${editorState.imageType || 'image/png'};base64,${editorState.imageBase64}` : editorState.imageUrl} />
          ) : <p className="map-library__empty">The picture you choose shows here.</p>
        ) : draft ? (
          <>
            <div className="map-library__save-bar" aria-label="Save the draft">
              <Form.Group controlId="map-draft-name" className="map-library__grow">
                <Form.Label>Name</Form.Label>
                <Form.Control size="sm" value={draftName} onChange={(event) => setDraftName(event.target.value)} disabled={saving} />
              </Form.Group>
              <FolderField id="map-draft-folder" folders={folders} selection={draftFolderSelection} value={draftFolder}
                onSelect={(event) => { setDraftFolderSelection(event.target.value); setDraftFolder(''); }}
                onType={(event) => setDraftFolder(event.target.value)} disabled={saving} />
              <Form.Check type="switch" id="map-draft-activate" label="Make it the active map" checked={draftActivate}
                onChange={(event) => setDraftActivate(event.target.checked)} disabled={saving} />
              <div className="map-library__row">
                <Button size="sm" variant="warning" onClick={saveDraft} disabled={saving || loading} data-testid="save-map-new-button">
                  {saving && saveMode === 'create' ? <><Spinner as="span" animation="border" size="sm" className="me-2" aria-hidden="true" />Saving…</> : 'Save to library'}
                </Button>
                {replaceable && (
                  <Button size="sm" variant="outline-light" onClick={replaceWithDraft} disabled={saving || loading} data-testid="save-map-update-button">
                    {saving && saveMode === 'update' ? 'Replacing…' : `Replace “${getMapDisplayTitle(selectedMap, DEFAULT_MAP_TITLE)}”`}
                  </Button>
                )}
                <Button size="sm" variant="link" className="map-library__discard" onClick={onDiscardDraft} disabled={saving}>Discard</Button>
              </div>
            </div>
            <div className="map-library__board" data-testid="map-preview-card">{board}</div>
          </>
        ) : (
          <p className="map-library__empty">{source === 'draw' ? 'Your generated map shows here, ready to save.' : 'Describe a map and generate it; the result shows here.'}</p>
        )}
      </section>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} fullscreen className="map-library-modal" aria-labelledby="map-library-title">
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title id="map-library-title">Maps</Modal.Title>
        <div className="map-library__modes" role="tablist" aria-label="Map window mode">
          <button type="button" role="tab" aria-selected={mode === 'library'} onClick={() => setMode('library')}>Library</button>
          <button type="button" role="tab" aria-selected={mode === 'create'} onClick={() => setMode('create')} data-testid="create-map-button">Create</button>
        </div>
      </Modal.Header>
      <Modal.Body className="map-library" data-testid="resource-map-card">
        {status && (
          <Alert variant={status.type} dismissible onClose={onDismissStatus} className="map-library__status">{status.message}</Alert>
        )}
        {mode === 'library' ? library : create}
      </Modal.Body>
    </Modal>
  );
}
